import { BeconCommonTypes, BeconScalpTypes, BeconUserTypes } from "@becon-team/becon-fe-lib/my-types";
import { zDateString } from "@becon-team/becon-fe-lib/my-zod";
import { HairTypeEnum } from "modules/account/services/account.dto";
import { z } from "zod";

// # V1: Before&After (lite)
export enum ComparisonV1SummaryItemEnum {
  SCALP_HEALTH_SCORE = "SCALP_HEALTH_SCORE",
  HAIR_FOLLICLE_DENSITY = BeconScalpTypes.HairLossItemEnum.HAIR_FOLLICLE_DENSITY,
  HAIR_QUANTITY = BeconScalpTypes.HairLossItemEnum.HAIR_QUANTITY,
  DEAD_SKIN = BeconScalpTypes.ScalpImageItemEnum.DEAD_SKIN,
  SENSITIVITY = BeconScalpTypes.ScalpImageItemEnum.SENSITIVITY,
  TEMPERATURE = BeconScalpTypes.ScalpSensorItemEnum.TEMPERATURE,
  WATER = BeconScalpTypes.ScalpSensorItemEnum.WATER,
}
export type ComparisonV1SummaryItem = keyof typeof ComparisonV1SummaryItemEnum;

export enum ScalpMicroPositionKeyEnum {
  forehead = "forehead",
  side_head = "side_head",
  hind_head = "hind_head",
  crown = "crown",
  forehead_center = "forehead_center",
  forehead_left = "forehead_left",
  forehead_right = "forehead_right",
  side_head_left = "side_head_left",
  side_head_right = "side_head_right",
}
export type ScalpPositionKey = keyof typeof ScalpMicroPositionKeyEnum;

export const ScalpTypesDtoSchema = z.object({
  primary: z.nativeEnum(BeconUserTypes.ScalpTypeEnum),
  secondary: z.nativeEnum(BeconUserTypes.ScalpTypeEnum).nullable(),
});
export type ScalpTypesDto = z.infer<typeof ScalpTypesDtoSchema>;

export const ScalpTypeRatiosDtoSchema = z.object({
  age_group: z.string(),
  all_records: z.number(),
  ratios: z.array(
    z.object({
      scalp_type: z.nativeEnum(BeconUserTypes.ScalpTypeEnum),
      num_records: z.number(),
      ratio: z.number(),
    }),
  ),
});
export type ScalpTypeRatiosDto = z.infer<typeof ScalpTypeRatiosDtoSchema>;

export const ScalpHexagonScoreDtoSchema = z.object({
  hair_follicle_density: z.number(),
  hair_per_hair_follicle: z.number(),
  hair_quantity: z.number(),
  hair_thickness: z.number(),
  sensitivity: z.number(),
  dead_skin: z.number(),
  temperature: z.number(),
  water: z.number(),
  oil: z.number(),
  gas: z.number(),
});
export type ScalpHexagonScoreDto = z.infer<typeof ScalpHexagonScoreDtoSchema>;
export type ScalpHexagonScoreKey = keyof ScalpHexagonScoreDto;

export const ScalpDetailItemDtoSchema = z.object({
  item: z.nativeEnum(BeconScalpTypes.ScalpMeasureItemEnum),
  images: z.array(
    z.object({
      image_url: z.string(),
      position: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum),
    }),
  ),
  measurements: z.object({
    my: z.number(),
    all_averages: z.number(),
    my_average: z.number(),
    top_10: z.number(),
  }),
});
export type ScalpDetailItemDto = z.infer<typeof ScalpDetailItemDtoSchema>;

const CrownMacroRatiosSchema = z.object({
  severe: z.number(),
  mild: z.number(),
  severe_average: z.number().optional(),
  mild_average: z.number().optional(),
});

export enum HairMacroImageTypeEnum {
  NORMAL = "NORMAL",
  MASKING = "MASKING",
}
export type HairMacroImageType = keyof typeof HairMacroImageTypeEnum;

export const CrownMacroDtoSchema = z.object({
  id: z.number(),
  source_images: z.nullable(
    z.object({
      normal: z.string(),
      masking: z.string(),
    }),
  ),
  ratio: CrownMacroRatiosSchema,
  simulation_result: z.array(
    z.object({
      simulation_count: z.number(),
      type: z.nativeEnum(HairMacroImageTypeEnum),
      image_url: z.string(),
    }),
  ),
  last_simulation_info: z
    .object({
      masking_image: z.string(),
      ratio: CrownMacroRatiosSchema.omit({ severe_average: true, mild_average: true }),
    })
    .nullable(),
});
export type CrownMacroDto = z.infer<typeof CrownMacroDtoSchema>;

export const UserInResultDtoSchema = z.object({
  user_id: z.string().nullable().optional(),
  phone_number: z.string().nullable().optional(),
  region: z.nativeEnum(BeconCommonTypes.RegionEnum).nullable().optional(),
  nickname: z.string().nullable().optional(),
  gender: z.nativeEnum(BeconUserTypes.UserGenderEnum).nullable().optional(),
  age_group: z.nativeEnum(BeconUserTypes.AgeGroupEnum).nullable().optional(),
  hair_loss_type: z.nativeEnum(BeconUserTypes.HairlossTypeEnum).nullable().optional(),
  hair_loss_level: z.number().nullable().optional(),
  hairloss_family_history: z.nativeEnum(BeconUserTypes.HairlossFamilyHistoryEnum).nullable().optional(),
  scalp_issue: z
    .object({
      issue_1: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).nullable().optional(),
      issue_2: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).nullable().optional(),
      issue_3: z.nativeEnum(BeconUserTypes.ScalpIssueTypeEnum).nullable().optional(),
      level_1: z.number().nullable().optional(),
      level_2: z.number().nullable().optional(),
      level_3: z.number().nullable().optional(),
    })
    .optional()
    .nullable(),
  hair_damage_level: z.number().nullable().optional(),
  skin_color: z.nativeEnum(BeconUserTypes.SkinColorEnum).nullable().optional(),
  hair_color: z.nativeEnum(BeconUserTypes.HairColorEnum).nullable().optional(),
  hair_type: z.nativeEnum(HairTypeEnum).nullable().optional(),
  lifestyle: z
    .object({
      dyeing: z.nativeEnum(BeconUserTypes.NoneOrPeriodicalEnum).nullable().optional(),
      perm: z.nativeEnum(BeconUserTypes.NoneOrPeriodicalEnum).nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type UserInResultDto = z.infer<typeof UserInResultDtoSchema>;

/** 두피종합분석 */
export const CompositeScalpResultDtoSchema = z.object({
  overview: z.object({
    composite_scalp_score: z.number(),
    group: z.object({
      age_group: z.nativeEnum(BeconUserTypes.AgeGroupEnum),
      gender: z.nativeEnum(BeconUserTypes.UserGenderEnum),
      num_records: z.number(),
      rank: z.number(),
      average: z.number(),
      top_10: z.number(),
    }),
  }),

  positions: z.array(
    z.object({
      position: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum),
      my_average: z.number(),
      image_urls: z.array(z.string()),
    }),
  ),
  user: UserInResultDtoSchema.nullable().optional(),
});
export type CompositeScalpResultDto = z.infer<typeof CompositeScalpResultDtoSchema>;

export const CompositeScalpDtoSchema = z.object({
  result: CompositeScalpResultDtoSchema.optional(),
});
export type CompositeScalpDto = z.infer<typeof CompositeScalpDtoSchema>;

export const ComparisonCandidateDtoSchema = z.object({
  candidate_id: z.number(),
  position: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum),
  image_url: z.string(),
  diagnosis_done: z.boolean(),
  diagnosis_id: z.number().nullable(),
  hairloss_index: z.number().nullable(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  reg_date: zDateString,
});
export type ComparisonCandidateDto = z.infer<typeof ComparisonCandidateDtoSchema>;

export const ComparisonSetResultV1DtoSchema = z.object({
  summary_items: z.array(
    z.object({
      type: z.nativeEnum(ComparisonV1SummaryItemEnum),
      before: z.number(),
      after: z.number(),
    }),
  ),
});
export type ComparisonSetResultV1Dto = z.infer<typeof ComparisonSetResultV1DtoSchema>;

// # V1Advanced: Before&After
export const ComparisonV1AdvancedSummaryItemEnum = {
  SCALP_HEALTH_SCORE: "SCALP_HEALTH_SCORE",
  ...BeconScalpTypes.ScalpMeasureItemEnum,
} as const;
export type ComparisonV1AdvancedSummaryItem = keyof typeof ComparisonV1AdvancedSummaryItemEnum;

export const ComparisonSetResultV1AdvancedDtoSchema = z.object({
  summary_items: z.array(
    z.object({
      type: z.nativeEnum(ComparisonV1AdvancedSummaryItemEnum),
      before: z.number(),
      after: z.number(),
    }),
  ),
});
export type ComparisonSetResultV1AdvancedDto = z.infer<typeof ComparisonSetResultV1AdvancedDtoSchema>;

// # V2Advanced: precision-hair-loss
export const ComparisonSetResultV2AdvancedDtoSchema = z.object({
  hairloss_health_scores: z.object({
    my: z.number(),
    all: z.number(),
    top_10: z.number(),
  }),
  candidates: z.object({
    before: z.array(ComparisonCandidateDtoSchema),
    after: z.array(ComparisonCandidateDtoSchema),
  }),
});
export type ComparisonSetResultV2AdvancedDto = z.infer<typeof ComparisonSetResultV2AdvancedDtoSchema>;

// ========== smp ========== //
const ForeheadImageUrlSchema = z.object({
  image_url: z.string().url(),
});

export const ForeheadMacroDtoSchema = z.object({
  normal: ForeheadImageUrlSchema,
  forehead: z.object({
    image_url: z.string(),
    my: z.number(),
    level: z.number(),
  }),
  aspect: z.object({
    image_url: z.string(),
    vertical: z.number(),
    level: z.number(),
  }),
  vertical: z.object({
    image_url: z.string(),
    upper: z.number(),
    lower: z.number(),
    level: z.number(),
  }),
  simulation: z.array(ForeheadImageUrlSchema),
  face_mesh: z
    .object({
      image_url: z.string(),
    })
    .nullable()
    .optional(),
});
export type ForeheadMacroDto = z.infer<typeof ForeheadMacroDtoSchema>;
export const MacroAnalysisDtoSchema = z.object({
  id: z.number(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
  image_url: z.string().nullable(),
});
export type MacroAnalysisDto = z.infer<typeof MacroAnalysisDtoSchema>;
export const SmpSetDtoSchema = z.object({
  id: z.number(),
  reg_date: z.string(),
  analysis_date: z.string().nullable(),
  crown: MacroAnalysisDtoSchema.nullable(),
  forehead: MacroAnalysisDtoSchema.nullable(),
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
});
export type SmpSetDto = z.infer<typeof SmpSetDtoSchema>;

export const SmpSetResultDtoSchema = z.object({
  crown: CrownMacroDtoSchema.nullable(),
  forehead: ForeheadMacroDtoSchema.nullable(),
  extras: z
    .object({
      user: UserInResultDtoSchema.nullable().optional(),
    })
    .optional()
    .nullable(),
});
export type SmpSetResultDto = z.infer<typeof SmpSetResultDtoSchema>;

export const ScalpCandidateDtoSchema = z.object({
  id: z.number(),
  image: z.string().nullable(),
  uv_image: z.string().nullable(),
  position: z.nativeEnum(BeconScalpTypes.ScalpPositionEnum).nullable(),
  reg_date: zDateString,
  status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
});
export type ScalpCandidateDto = z.infer<typeof ScalpCandidateDtoSchema>;

export const ScalpSetItemsDtoSchema = z.array(
  z.object({
    id: z.number(),
    status: z.nativeEnum(BeconCommonTypes.SetStatusEnum),
    analysis_date: zDateString.nullable(),
    reg_date: zDateString.nullable(),
    candidates: z.array(ScalpCandidateDtoSchema),
  }),
);
export type ScalpSetItemsDto = z.infer<typeof ScalpSetItemsDtoSchema>;

export const ScalpSetHistoryDtoSchema = z.object({
  stats: z.array(
    z.object({
      date: zDateString,
      average_score: z.number(),
    }),
  ),
  items: ScalpSetItemsDtoSchema,
});
export type ScalpSetHistoryDto = z.infer<typeof ScalpSetHistoryDtoSchema>;

/** GET: ScalpSet 조회 */
export const GetScalpsetResponseSchema = z.object({
  scalp_sets: z.object({
    total: z.number(),
    skip: z.number(),
    take: z.number(),
    items: ScalpSetItemsDtoSchema,
  }),
});

export type GetScalpsetResponse = z.infer<typeof GetScalpsetResponseSchema>;

export interface GetScalpset {
  (arg: {
    userId: string;
    skip?: number;
    take: number;
    status?: BeconCommonTypes.SetStatus;
  }): Promise<GetScalpsetResponse>;
}
